import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';

export default function TradeValidator() {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-fetched data
  const [marketData, setMarketData] = useState(null);
  const [spxData, setSpxData] = useState(null);
  const [vixData, setVixData] = useState(null);

  // Manual override option
  const [manualMode, setManualMode] = useState(false);
  const [volumeDay1, setVolumeDay1] = useState('');
  const [volumeDay2, setVolumeDay2] = useState('');
  const [volumeDay3, setVolumeDay3] = useState('');
  const [stochRsi, setStochRsi] = useState('');
  const [day1High, setDay1High] = useState('');
  const [day1Low, setDay1Low] = useState('');
  const [day2High, setDay2High] = useState('');
  const [day2Low, setDay2Low] = useState('');
  const [day3High, setDay3High] = useState('');
  const [day3Low, setDay3Low] = useState('');

  const [entry, setEntry] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [accountSize, setAccountSize] = useState('');
  const [riskPercent, setRiskPercent] = useState('2');

  const [validation, setValidation] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('tradeHistory');
    if (saved) {
      setTradeHistory(JSON.parse(saved));
    }
  }, []);
// ---------- Indicators ----------
  const calculateStochRSI = (prices, period = 14, stochPeriod = 14) => {
    if (!Array.isArray(prices) || prices.length < period + stochPeriod) return null;

    // RSI seed
    let gains = 0, losses = 0;
    for (let i = 1; i <= period; i++) {
      const change = prices[i] - prices[i - 1];
      if (!Number.isFinite(change)) return null;
      if (change > 0) gains += change;
      else losses += Math.abs(change);
    }

    let avgGain = gains / period;
    let avgLoss = losses / period;

    const rsiValues = [];
    for (let i = period; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      if (!Number.isFinite(change)) return null;

      if (change > 0) {
        avgGain = (avgGain * (period - 1) + change) / period;
        avgLoss = (avgLoss * (period - 1)) / period;
      } else {
        avgGain = (avgGain * (period - 1)) / period;
        avgLoss = (avgLoss * (period - 1) + Math.abs(change)) / period;
      }

      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);
      rsiValues.push(rsi);
    }

    if (rsiValues.length < stochPeriod) return null;

    const recentRSI = rsiValues.slice(-stochPeriod);
    const minRSI = Math.min(...recentRSI);
    const maxRSI = Math.max(...recentRSI);
    const currentRSI = recentRSI[recentRSI.length - 1];

    if (!Number.isFinite(currentRSI) || maxRSI === minRSI) return null;

    return ((currentRSI - minRSI) / (maxRSI - minRSI)) * 100;
  };

  const calculateATR = (highs, lows, closes, period = 14) => {
    if (
      !Array.isArray(highs) ||
      !Array.isArray(lows) ||
      !Array.isArray(closes) ||
      highs.length < period + 1 ||
      lows.length < period + 1 ||
      closes.length < period + 1
    ) {
      return null;
    }

    const trueRanges = [];
    for (let i = 1; i < highs.length; i++) {
      const high = highs[i];
      const low = lows[i];
      const prevClose = closes[i - 1];
      if (![high, low, prevClose].every(Number.isFinite)) continue;

      const tr = Math.max(
        high - low,
        Math.abs(high - prevClose),
        Math.abs(low - prevClose)
      );
      trueRanges.push(tr);
    }

    if (trueRanges.length < period) return null;
    const recentTR = trueRanges.slice(-period);
    const atr = recentTR.reduce((sum, tr) => sum + tr, 0) / period;
    return atr;
  };

// ---------- Data Fetch ----------
  const fetchMarketData = async () => {
    if (!symbol.trim()) {
      setError('Please enter a symbol');
      return;
    }

    setLoading(true);
    setError('');
    setMarketData(null);
    setSpxData(null);
    setVixData(null);
    setValidation(null);

    try {
      const yahooSymbol = symbol.toUpperCase().trim();
      const endDate = Math.floor(Date.now() / 1000);
      const startDate = endDate - 60 * 24 * 60 * 60; // ~60 days

      // Stock data (with CORS proxy)
      const stockResp = await fetch(
        `https://api.allorigins.win/get?url=${encodeURIComponent(`https://query1.finance.yahoo.com/v8/finance/chart/${yahooSymbol}?period1=${startDate}&period2=${endDate}&interval=1d`)}`
      );
      if (!stockResp.ok) throw new Error('Unable to fetch stock data. Please check the symbol.');
      
      const stockProxyData = await stockResp.json();
      const stockJson = JSON.parse(stockProxyData.contents);
      const stockResult = stockJson.chart?.result?.[0];
      if (!stockResult) throw new Error('No data found for this symbol');

      const quotes = stockResult.indicators?.quote?.[0] || {};
      const timestamps = stockResult.timestamp || [];

      if (!Array.isArray(quotes.close) || quotes.close.length < 6) {
        throw new Error('Insufficient data for analysis');
      }

      const len = quotes.close.length;

      const day3 = {
        high: quotes.high[len - 1],
        low: quotes.low[len - 1],
        close: quotes.close[len - 1],
        volume: quotes.volume[len - 1],
        date: new Date(timestamps[len - 1] * 1000).toLocaleDateString(),
      };
      const day2 = {
        high: quotes.high[len - 2],
        low: quotes.low[len - 2],
        close: quotes.close[len - 2],
        volume: quotes.volume[len - 2],
        date: new Date(timestamps[len - 2] * 1000).toLocaleDateString(),
      };
      const day1 = {
        high: quotes.high[len - 3],
        low: quotes.low[len - 3],
        close: quotes.close[len - 3],
        volume: quotes.volume[len - 3],
        date: new Date(timestamps[len - 3] * 1000).toLocaleDateString(),
      };

      const closePrices = quotes.close.filter((p) => p != null);
      const currentPrice = closePrices.at(-1);
      const stochRSIValue = calculateStochRSI(closePrices);

      const highs = (quotes.high || []).filter((v) => v != null);
      const lows = (quotes.low || []).filter((v) => v != null);
      const atr = calculateATR(highs, lows, closePrices);
      const atrPercent = Number.isFinite(atr) && Number.isFinite(currentPrice)
        ? (atr / currentPrice) * 100
        : null;

      const stock5DayReturn =
        closePrices.length >= 6 && Number.isFinite(closePrices.at(-6))
          ? ((closePrices.at(-1) - closePrices.at(-6)) / closePrices.at(-6)) * 100
          : null;

      setMarketData({
        symbol: yahooSymbol,
        day1,
        day2,
        day3,
        stochRSI: stochRSIValue,
        currentPrice,
        atr,
        atrPercent,
        performance5d: stock5DayReturn,
      });
// SPX (with CORS proxy)
      const spxResp = await fetch(
        `https://api.allorigins.win/get?url=${encodeURIComponent(`https://query1.finance.yahoo.com/v8/finance/chart/%5EGSPC?period1=${startDate}&period2=${endDate}&interval=1d`)}`
      );
      const spxProxyData = await spxResp.json();
      const spxJson = JSON.parse(spxProxyData.contents);
      const spxResult = spxJson.chart?.result?.[0];
      const spxQuotes = spxResult?.indicators?.quote?.[0];

      if (spxQuotes && Array.isArray(spxQuotes.close)) {
        const spxClose = spxQuotes.close.filter((p) => p != null);
        if (spxClose.length >= 21) {
          const spxCurrent = spxClose.at(-1);
          const spx5DaysAgo = spxClose.at(-6);
          const spx20DaysAgo = spxClose.at(-21);

          const spx5DayReturn =
            Number.isFinite(spx5DaysAgo)
              ? ((spxCurrent - spx5DaysAgo) / spx5DaysAgo) * 100
              : null;
          const spx20DayReturn =
            Number.isFinite(spx20DaysAgo)
              ? ((spxCurrent - spx20DaysAgo) / spx20DaysAgo) * 100
              : null;

          const spxRecent20 = spxClose.slice(-20);
          const spx20MA =
            spxRecent20.reduce((sum, p) => sum + p, 0) / 20;
          const spxTrend = spxCurrent > spx20MA ? 'bullish' : 'bearish';

          const relativeStrength =
            Number.isFinite(stock5DayReturn) && Number.isFinite(spx5DayReturn)
              ? stock5DayReturn - spx5DayReturn
              : null;

          setSpxData({
            current: spxCurrent,
            performance5d: spx5DayReturn,
            performance20d: spx20DayReturn,
            trend: spxTrend,
            ma20: spx20MA,
            relativeStrength,
          });
        } else {
          setSpxData(null);
        }
      }

      // VIX (with CORS proxy)
      const vixResp = await fetch(
        `https://api.allorigins.win/get?url=${encodeURIComponent(`https://query1.finance.yahoo.com/v8/finance/chart/%5EVIX?period1=${startDate}&period2=${endDate}&interval=1d`)}`
      );
      const vixProxyData = await vixResp.json();
      const vixJson = JSON.parse(vixProxyData.contents);
      const vixResult = vixJson.chart?.result?.[0];
      const vixQuotes = vixResult?.indicators?.quote?.[0];

      if (vixQuotes && Array.isArray(vixQuotes.close)) {
        const vixClose = vixQuotes.close.filter((p) => p != null);
        if (vixClose.length > 0) {
          const vixCurrent = vixClose.at(-1);
          let vixLevel = 'low';
          if (vixCurrent > 30) vixLevel = 'extreme';
          else if (vixCurrent > 20) vixLevel = 'elevated';
          else if (vixCurrent > 15) vixLevel = 'moderate';

          setVixData({ current: vixCurrent, level: vixLevel });
        } else {
          setVixData(null);
        }
      }

      // Auto-populate entry, stop loss, and take profit
      if (Number.isFinite(currentPrice)) {
        setEntry(currentPrice.toFixed(2));
        
        // Auto-suggest Stop Loss and Take Profit based on ATR
        if (Number.isFinite(atr)) {
          // Stop Loss: Entry - (2 × ATR) - conservative for swing trades
          const suggestedStopLoss = currentPrice - (2 * atr);
          setStopLoss(suggestedStopLoss.toFixed(2));
          
          // Take Profit: Entry + (3 × ATR) - gives 1.5:1 reward/risk ratio
          const suggestedTakeProfit = currentPrice + (3 * atr);
          setTakeProfit(suggestedTakeProfit.toFixed(2));
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch market data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

// ---------- Validation ----------
  const validateSetup = () => {
    let vol1, vol2, vol3, rsi, high1, low1, high2, low2, high3, low3;

    if (manualMode) {
      vol1 = parseFloat(volumeDay1);
      vol2 = parseFloat(volumeDay2);
      vol3 = parseFloat(volumeDay3);
      rsi = parseFloat(stochRsi);
      high1 = parseFloat(day1High);
      low1 = parseFloat(day1Low);
      high2 = parseFloat(day2High);
      low2 = parseFloat(day2Low);
      high3 = parseFloat(day3High);
      low3 = parseFloat(day3Low);
    } else if (marketData) {
      vol1 = marketData.day1.volume;
      vol2 = marketData.day2.volume;
      vol3 = marketData.day3.volume;
      rsi = marketData.stochRSI;
      high1 = marketData.day1.high;
      low1 = marketData.day1.low;
      high2 = marketData.day2.high;
      low2 = marketData.day2.low;
      high3 = marketData.day3.high;
      low3 = marketData.day3.low;
    } else {
      setError('Please fetch market data first');
      return;
    }

    const entryPrice = parseFloat(entry);
    const sl = parseFloat(stopLoss);
    const tp = parseFloat(takeProfit);
    const accSize = parseFloat(accountSize);
    const riskPct = parseFloat(riskPercent);

    // Strategy checks (with guards)
    const volOk = [vol1, vol2, vol3].every(Number.isFinite);
    const highsOk = [high1, high2, high3].every(Number.isFinite);
    const lowsOk = [low1, low2, low3].every(Number.isFinite);
    const rsiOk = Number.isFinite(rsi);

    const strategyChecks = {
      volumeIncreasing: volOk ? vol2 > vol1 && vol3 > vol2 : false,
      stochOversold: rsiOk ? rsi < 20 : false,
      higherHighs: highsOk ? high2 > high1 && high3 > high2 : false,
      noLowerLows: lowsOk ? low2 >= low1 && low3 >= low2 : false,
    };

    const strategyPassed = Object.values(strategyChecks).filter(Boolean).length;
    const strategyTotal = Object.keys(strategyChecks).length;
    const strategyScore = (strategyPassed / strategyTotal) * 100;

    // Market checks (count only booleans)
    const marketChecksRaw = {
      spxBullish: spxData ? spxData.trend === 'bullish' : null,
      spxPositive: spxData && Number.isFinite(spxData.performance5d) ? spxData.performance5d > 0 : null,
      relativeStrength: spxData && Number.isFinite(spxData.relativeStrength) ? spxData.relativeStrength > 0 : null,
      lowVolatility: vixData && Number.isFinite(vixData.current) ? vixData.current < 25 : null,
    };
    const marketValues = Object.values(marketChecksRaw).filter((v) => v !== null);
    const marketPassed = marketValues.filter((v) => v === true).length;
    const marketTotal = marketValues.length;
    const marketScore = marketTotal > 0 ? (marketPassed / marketTotal) * 100 : 0;
    const marketChecks = marketChecksRaw;

    // Overall
    const overallScore = strategyScore * 0.7 + marketScore * 0.3;

    // Risk math (safe)
    let riskReward = null;
    let positionSize = null;
    let dollarRisk = null;

    if ([entryPrice, sl, tp, accSize, riskPct].every(Number.isFinite)) {
      const riskPerShare = Math.abs(entryPrice - sl);
      const rewardPerShare = Math.abs(tp - entryPrice);

      if (riskPerShare > 0) {
        riskReward = rewardPerShare / riskPerShare;
        dollarRisk = (accSize * riskPct) / 100;
        positionSize = Math.floor(dollarRisk / riskPerShare);
      }
    }

    setValidation({
      strategyChecks,
      marketChecks,
      strategyScore,
      marketScore,
      overallScore,
      strategyPassed,
      strategyTotal,
      marketPassed,
      marketTotal,
      riskReward,
      positionSize,
      dollarRisk,
      timestamp: new Date().toISOString(),
    });
  };

// ---------- Trade log ----------
  const logTrade = (action) => {
    if (!validation) return;

    const trade = {
      id: Date.now(),
      symbol: manualMode ? symbol.toUpperCase() : marketData?.symbol ?? symbol.toUpperCase(),
      action,
      strategyScore: validation.strategyScore,
      marketScore: validation.marketScore,
      overallScore: validation.overallScore,
      strategyChecks: validation.strategyChecks,
      marketChecks: validation.marketChecks,
      riskReward: validation.riskReward,
      entry: Number.parseFloat(entry),
      stopLoss: Number.parseFloat(stopLoss),
      takeProfit: Number.parseFloat(takeProfit),
      positionSize: validation.positionSize,
      timestamp: new Date().toLocaleString(),
    };

    const updated = [trade, ...tradeHistory];
    setTradeHistory(updated);
    localStorage.setItem('tradeHistory', JSON.stringify(updated));
  };

  const clearHistory = () => {
    setTradeHistory([]);
    localStorage.removeItem('tradeHistory');
  };

  const CheckItem = ({ label, passed, details }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      {passed === null ? (
        <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
      ) : passed ? (
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
      ) : (
        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1">
        <div className="font-medium text-gray-900">{label}</div>
        {details && <div className="text-sm text-gray-600 mt-1">{details}</div>}
      </div>
    </div>
  );

  // ---------- UI ----------
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-900">Trade Setup Validator</h1>
          </div>

          {/* Symbol Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Stock Symbol</label>
            <div className="flex gap-3">
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && fetchMarketData()}
                placeholder="e.g. AAPL, TSLA, MSFT"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
              />
              <button
                type="button"
                onClick={fetchMarketData}
                disabled={loading}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Fetch Data
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="manualMode"
                checked={manualMode}
                onChange={(e) => setManualMode(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="manualMode" className="text-sm text-gray-600">
                Manual entry mode (if auto-fetch fails)
              </label>
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
          {/* Market Context Dashboard */}
          {(spxData || vixData) && (
            <div className="mb-6 grid md:grid-cols-2 gap-4">
              {spxData && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      {spxData.trend === 'bullish' ? (
                        <TrendingUp className="w-5 h-5" />
                      ) : (
                        <TrendingDown className="w-5 h-5" />
                      )}
                      S&amp;P 500 Context
                    </h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-bold ${
                        spxData.trend === 'bullish'
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}
                    >
                      {spxData.trend.toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">5-Day Performance:</span>
                      <span
                        className={`font-bold ${
                          (spxData.performance5d ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {Number.isFinite(spxData.performance5d)
                          ? `${spxData.performance5d >= 0 ? '+' : ''}${spxData.performance5d.toFixed(2)}%`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">20-Day Performance:</span>
                      <span
                        className={`font-bold ${
                          (spxData.performance20d ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {Number.isFinite(spxData.performance20d)
                          ? `${spxData.performance20d >= 0 ? '+' : ''}${spxData.performance20d.toFixed(2)}%`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Relative Strength:</span>
                      <span
                        className={`font-bold ${
                          (spxData.relativeStrength ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {Number.isFinite(spxData.relativeStrength)
                          ? `${spxData.relativeStrength >= 0 ? '+' : ''}${spxData.relativeStrength.toFixed(2)}%`
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {vixData && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-3">Market Volatility (VIX)</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-purple-900">
                        {Number.isFinite(vixData.current) ? vixData.current.toFixed(2) : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Current VIX Level</div>
                    </div>
                    <div
                      className={`px-4 py-2 rounded-lg font-bold ${
                        vixData.level === 'low'
                          ? 'bg-green-200 text-green-800'
                          : vixData.level === 'moderate'
                          ? 'bg-yellow-200 text-yellow-800'
                          : vixData.level === 'elevated'
                          ? 'bg-orange-200 text-orange-800'
                          : 'bg-red-200 text-red-800'
                      }`}
                    >
                      {vixData.level?.toUpperCase() ?? 'N/A'}
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-600">
                    Low: &lt;15 | Moderate: 15–20 | Elevated: 20–30 | Extreme: &gt;30
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stock Data Display */}
          {marketData && !manualMode && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-green-900">Data Fetched for {marketData.symbol}</h3>
              </div>

              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white p-3 rounded">
                  <div className="text-gray-600 mb-1 text-xs">{marketData.day1.date}</div>
                  <div className="font-mono text-sm">H: ${marketData.day1.high.toFixed(2)}</div>
                  <div className="font-mono text-sm">L: ${marketData.day1.low.toFixed(2)}</div>
                  <div className="font-mono text-xs text-gray-600">Vol: {(marketData.day1.volume / 1_000_000).toFixed(1)}M</div>
                </div>
                <div className="bg-white p-3 rounded">
                  <div className="text-gray-600 mb-1 text-xs">{marketData.day2.date}</div>
                  <div className="font-mono text-sm">H: ${marketData.day2.high.toFixed(2)}</div>
                  <div className="font-mono text-sm">L: ${marketData.day2.low.toFixed(2)}</div>
                  <div className="font-mono text-xs text-gray-600">Vol: {(marketData.day2.volume / 1_000_000).toFixed(1)}M</div>
                </div>
                <div className="bg-white p-3 rounded">
                  <div className="text-gray-600 mb-1 text-xs">{marketData.day3.date}</div>
                  <div className="font-mono text-sm">H: ${marketData.day3.high.toFixed(2)}</div>
                  <div className="font-mono text-sm">L: ${marketData.day3.low.toFixed(2)}</div>
                  <div className="font-mono text-xs text-gray-600">Vol: {(marketData.day3.volume / 1_000_000).toFixed(1)}M</div>
                </div>
                <div className="bg-white p-3 rounded">
                  <div className="text-gray-600 mb-1 text-xs">Indicators</div>
                  <div className="text-sm">
                    <span className="text-gray-600">Stoch RSI: </span>
                    <span
                      className={`font-bold ${
                        Number.isFinite(marketData.stochRSI) && marketData.stochRSI < 20
                          ? 'text-green-600'
                          : 'text-gray-900'
                      }`}
                    >
                      {Number.isFinite(marketData.stochRSI) ? marketData.stochRSI.toFixed(1) : 'N/A'}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">ATR: </span>
                    <span className="font-bold">
                      {Number.isFinite(marketData.atrPercent) ? `${marketData.atrPercent.toFixed(2)}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {Number.isFinite(marketData.atr) && `$${marketData.atr.toFixed(2)} per share`}
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Price: </span>
                    <span className="font-bold text-indigo-600">
                      {Number.isFinite(marketData.currentPrice) ? `$${marketData.currentPrice.toFixed(2)}` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Manual Mode Inputs */}
          {manualMode && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-semibold text-yellow-900 mb-3">Manual Data Entry</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day 1</label>
                  <input type="number" value={day1High} onChange={(e) => setDay1High(e.target.value)} placeholder="High" className="w-full px-3 py-2 border rounded-lg mb-1" />
                  <input type="number" value={day1Low} onChange={(e) => setDay1Low(e.target.value)} placeholder="Low" className="w-full px-3 py-2 border rounded-lg" />
                  <input type="number" value={volumeDay1} onChange={(e) => setVolumeDay1(e.target.value)} placeholder="Volume" className="w-full px-3 py-2 border rounded-lg mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day 2</label>
                  <input type="number" value={day2High} onChange={(e) => setDay2High(e.target.value)} placeholder="High" className="w-full px-3 py-2 border rounded-lg mb-1" />
                  <input type="number" value={day2Low} onChange={(e) => setDay2Low(e.target.value)} placeholder="Low" className="w-full px-3 py-2 border rounded-lg" />
                  <input type="number" value={volumeDay2} onChange={(e) => setVolumeDay2(e.target.value)} placeholder="Volume" className="w-full px-3 py-2 border rounded-lg mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day 3</label>
                  <input type="number" value={day3High} onChange={(e) => setDay3High(e.target.value)} placeholder="High" className="w-full px-3 py-2 border rounded-lg mb-1" />
                  <input type="number" value={day3Low} onChange={(e) => setDay3Low(e.target.value)} placeholder="Low" className="w-full px-3 py-2 border rounded-lg" />
                  <input type="number" value={volumeDay3} onChange={(e) => setVolumeDay3(e.target.value)} placeholder="Volume" className="w-full px-3 py-2 border rounded-lg mt-1" />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stochastic RSI (0–100)
                </label>
                <input
                  type="number"
                  value={stochRsi}
                  onChange={(e) => setStochRsi(e.target.value)}
                  placeholder="0-100"
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
          )}

          {/* Risk Management */}
          <div className="bg-indigo-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Risk Management</h3>
            {marketData && marketData.atr && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-blue-900">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">ATR-Based Suggestions:</span>
                  <span>Stop Loss at 2×ATR | Take Profit at 3×ATR (1.5:1 R:R)</span>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-5 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Size ($)</label>
                <input
                  type="number"
                  value={accountSize}
                  onChange={(e) => setAccountSize(e.target.value)}
                  placeholder="10000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk %</label>
                <input
                  type="number"
                  value={riskPercent}
                  onChange={(e) => setRiskPercent(e.target.value)}
                  placeholder="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entry Price</label>
                <input
                  type="number"
                  value={entry}
                  onChange={(e) => setEntry(e.target.value)}
                  placeholder="150.50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stop Loss {marketData && marketData.atr && <span className="text-xs text-blue-600">(Auto: 2×ATR)</span>}
                </label>
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder="148.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Take Profit {marketData && marketData.atr && <span className="text-xs text-blue-600">(Auto: 3×ATR)</span>}
                </label>
                <input
                  type="number"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder="157.50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={validateSetup}
            disabled={(!marketData && !manualMode)}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
          >
            Validate Setup
          </button>
          {/* Validation Results */}
          {validation && (
            <div className="mt-8 space-y-6">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-xl">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Strategy Score</h3>
                    <div className="text-3xl font-bold">{validation.strategyScore.toFixed(0)}%</div>
                    <p className="text-indigo-100 text-sm mt-1">
                      {validation.strategyPassed} of {validation.strategyTotal} conditions
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Market Context</h3>
                    <div className="text-3xl font-bold">{validation.marketScore.toFixed(0)}%</div>
                    <p className="text-indigo-100 text-sm mt-1">
                      {validation.marketPassed} of {validation.marketTotal} favorable
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Overall Score</h3>
                    <div className="text-3xl font-bold">{validation.overallScore.toFixed(0)}%</div>
                    <p className="text-indigo-100 text-sm mt-1">
                      {validation.overallScore >= 80 ? '✓ Excellent' : validation.overallScore >= 60 ? '⚠ Good' : '✗ Weak'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Strategy Checklist</h4>
                  <div className="space-y-2">
                    <CheckItem
                      label="Volume Increasing"
                      passed={validation.strategyChecks.volumeIncreasing}
                      details="Each day's volume should be higher than the previous"
                    />
                    <CheckItem
                      label="Stochastic RSI Oversold"
                      passed={validation.strategyChecks.stochOversold}
                      details="Stoch RSI should be below 20"
                    />
                    <CheckItem
                      label="Higher Highs"
                      passed={validation.strategyChecks.higherHighs}
                      details="Each day's high should exceed the previous day"
                    />
                    <CheckItem
                      label="No Lower Lows"
                      passed={validation.strategyChecks.noLowerLows}
                      details="Each day's low should be equal or higher"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Market Context Checklist</h4>
                  <div className="space-y-2">
                    <CheckItem
                      label="S&P 500 Bullish Trend"
                      passed={validation.marketChecks.spxBullish}
                      details="SPX trading above 20-day MA"
                    />
                    <CheckItem
                      label="S&P 500 Positive Momentum"
                      passed={validation.marketChecks.spxPositive}
                      details="SPX up over last 5 days"
                    />
                    <CheckItem
                      label="Relative Strength"
                      passed={validation.marketChecks.relativeStrength}
                      details="Stock outperforming SPX"
                    />
                    <CheckItem
                      label="Low Volatility"
                      passed={validation.marketChecks.lowVolatility}
                      details="VIX below 25 (lower risk environment)"
                    />
                  </div>
                </div>
              </div>

              {validation.riskReward && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Risk Analysis</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Risk:Reward Ratio</span>
                      <span
                        className={`font-bold ${
                          validation.riskReward >= 2
                            ? 'text-green-600'
                            : validation.riskReward >= 1.5
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        1:{validation.riskReward.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dollar Risk</span>
                      <span className="font-bold text-gray-900">
                        ${validation.dollarRisk?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Position Size</span>
                      <span className="font-bold text-gray-900">
                        {validation.positionSize?.toFixed(0)} shares
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {validation.strategyScore === 100 &&
                validation.marketScore >= 75 &&
                validation.riskReward >= 1.5 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-900 text-lg">🎯 HIGH CONFIDENCE SETUP</h4>
                        <p className="text-sm text-green-700 mt-1">
                          All strategy conditions met, favorable market context, and good risk:reward. This is a premium entry opportunity.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {validation.strategyScore === 100 && validation.marketScore < 50 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 text-lg">⚠ CAUTION: Weak Market Context</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Your strategy signals are good, but market conditions are not favorable. Consider waiting or reducing position size.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => logTrade('ENTERED')}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Log as ENTERED
                </button>
                <button
                  type="button"
                  onClick={() => logTrade('SKIPPED')}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                >
                  Log as SKIPPED
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Trade History */}
        {tradeHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Trade Log</h2>
              <button
                type="button"
                onClick={clearHistory}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear History
              </button>
            </div>
            <div className="space-y-3">
              {tradeHistory.map((trade) => (
                <div
                  key={trade.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-gray-900">{trade.symbol}</span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          trade.action === 'ENTERED'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {trade.action}
                      </span>
                      <span className="text-sm text-gray-500">{trade.timestamp}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-indigo-600">
                        {trade.overallScore.toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-600">
                        Strategy: {trade.strategyScore.toFixed(0)}% | Market:{' '}
                        {trade.marketScore.toFixed(0)}%
                      </div>
                      {Number.isFinite(trade.riskReward) && (
                        <div className="text-sm text-gray-600">
                          R:R 1:{trade.riskReward.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Strategy</div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className={trade.strategyChecks.volumeIncreasing ? 'text-green-600' : 'text-red-600'}>
                          {trade.strategyChecks.volumeIncreasing ? '✓' : '✗'} Volume
                        </div>
                        <div className={trade.strategyChecks.stochOversold ? 'text-green-600' : 'text-red-600'}>
                          {trade.strategyChecks.stochOversold ? '✓' : '✗'} Stoch RSI
                        </div>
                        <div className={trade.strategyChecks.higherHighs ? 'text-green-600' : 'text-red-600'}>
                          {trade.strategyChecks.higherHighs ? '✓' : '✗'} Higher Highs
                        </div>
                        <div className={trade.strategyChecks.noLowerLows ? 'text-green-600' : 'text-red-600'}>
                          {trade.strategyChecks.noLowerLows ? '✓' : '✗'} No Lower Lows
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Market Context</div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className={trade.marketChecks.spxBullish === true ? 'text-green-600' : trade.marketChecks.spxBullish === false ? 'text-red-600' : 'text-gray-400'}>
                          {trade.marketChecks.spxBullish === true ? '✓' : trade.marketChecks.spxBullish === false ? '✗' : '-'} SPX Trend
                        </div>
                        <div className={trade.marketChecks.spxPositive === true ? 'text-green-600' : trade.marketChecks.spxPositive === false ? 'text-red-600' : 'text-gray-400'}>
                          {trade.marketChecks.spxPositive === true ? '✓' : trade.marketChecks.spxPositive === false ? '✗' : '-'} SPX +
                        </div>
                        <div className={trade.marketChecks.relativeStrength === true ? 'text-green-600' : trade.marketChecks.relativeStrength === false ? 'text-red-600' : 'text-gray-400'}>
                          {trade.marketChecks.relativeStrength === true ? '✓' : trade.marketChecks.relativeStrength === false ? '✗' : '-'} Rel Str
                        </div>
                        <div className={trade.marketChecks.lowVolatility === true ? 'text-green-600' : trade.marketChecks.lowVolatility === false ? 'text-red-600' : 'text-gray-400'}>
                          {trade.marketChecks.lowVolatility === true ? '✓' : trade.marketChecks.lowVolatility === false ? '✗' : '-'} Low VIX
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
                                                          
